'use client';
import { ROUTES } from '@/constants/routes';
import { useCredentialsLogin } from '@/hooks/useCredentialsLogin';
import useGoogleLogin from '@/hooks/useGoogleLogin';
import { LoginCredentials } from '@/types/auth';
import { SESSION_STORAGE } from '@/types/sessionStorage';
import {
  Button,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toSentenceCase } from '@treyd-io/core/utils/string';
import { useToastNotification } from '@treyd-io/treyd-ui/hooks/useToastNotification';
import EyeIcon from '@treyd-io/treyd-ui/icons/EyeIcon';
import EyeOffIcon from '@treyd-io/treyd-ui/icons/EyeOffIcon';
import GoogleIcon from '@treyd-io/treyd-ui/icons/GoogleIcon';
import { useFormik } from 'formik';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEffectOnce, useSessionStorage } from 'react-use';
import * as Yup from 'yup';

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation();
  const searchParams = useSearchParams();
  const [, setCallbackUrl] = useSessionStorage(SESSION_STORAGE.CALLBACK_URL);
  const { mutate: googleSignIn, isPending: googleLoading } = useGoogleLogin();
  const { mutate: credentialsSignIn, isPending: credentialsLoading } =
    useCredentialsLogin();
  const showToastMessage = useToastNotification();

  const error = searchParams.get('error');
  useEffectOnce(() => {
    setCallbackUrl(searchParams.get('callbackUrl'));
  });

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleFormSubmit = async (values: LoginCredentials) => {
    credentialsSignIn(values);
  };

  const formik = useFormik<LoginCredentials>({
    initialValues: { email: '', password: '' },
    validationSchema: Yup.object({
      email: Yup.string().email('Invalid email address').required('Required'),
      password: Yup.string()
        .min(8, 'Must be at least 8 characters')
        .required('Required'),
    }),
    onSubmit: handleFormSubmit,
  });

  const isInvalidPassword =
    formik.touched.password && Boolean(formik.errors.password);

  useEffect(() => {
    error &&
      showToastMessage({ message: toSentenceCase(error), type: 'error' });
  }, [error]);

  return (
    <Stack maxWidth={400} gap={3} width="100%">
      <Stack gap={1}>
        <Typography variant="h4" color="text.primary">
          {t('welcome_back')}!
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('log_in_to_your_account_to_get_started')}
        </Typography>
      </Stack>
      <Stack gap={2.5}>
        <Button
          color="secondary"
          variant="outlined"
          size="large"
          fullWidth
          type="button"
          startIcon={<GoogleIcon />}
          onClick={() => googleSignIn()}
          disabled={googleLoading || credentialsLoading}>
          {t('continueWithGoogle')}
        </Button>
        <Divider>
          <Typography variant="body2" color="text.secondary">
            {t('or')}
          </Typography>
        </Divider>
        <form noValidate onSubmit={formik.handleSubmit}>
          <Stack gap={3} width="100%">
            <TextField
              fullWidth
              name="email"
              label={t('email')}
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            <Stack gap={1.5}>
              <FormControl fullWidth>
                <TextField
                  fullWidth
                  id="password"
                  name="password"
                  label={t('password')}
                  type={showPassword ? 'text' : 'password'}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={t('toggle_password_visibility')}
                            onClick={handleClickShowPassword}
                            color={isInvalidPassword ? 'error' : undefined}>
                            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  error={isInvalidPassword}
                />
                {isInvalidPassword && (
                  <FormHelperText error>
                    {formik.errors.password}
                  </FormHelperText>
                )}
              </FormControl>
              <Typography
                component={Link}
                href={ROUTES.forgotPassword}
                alignSelf="flex-start"
                variant="body1"
                color="info.main">
                {t('forgot_your_password')}
              </Typography>
            </Stack>
            <Button
              color="primary"
              variant="contained"
              size="large"
              fullWidth
              disabled={credentialsLoading || googleLoading}
              type="submit">
              {t('login')}
            </Button>
          </Stack>
        </form>
      </Stack>

      <Stack alignItems="center" gap={0.5} direction="row" flexWrap={'wrap'}>
        <Typography variant="body1" color="text.primary">
          {t('doNotHaveAccount')}
        </Typography>
        <Typography
          component={Link}
          href={ROUTES.signup}
          variant="body1"
          color="info.main">
          {t('signup')}
        </Typography>
      </Stack>
    </Stack>
  );
}
